/**
 * CRUD API FACTORY — genera i 5 metodi REST standard condivisi da ogni `*Api.ts`
 * del core, che seguono la convenzione plurale/singolare del CRM:
 *
 *   getAll(groupId, params?) -> Page<T>   GET  {collection}/{groupId}   (+ page/pageSize/sort/search)
 *   getById(id)              -> T         GET  {single}/{id}
 *   create(groupId, payload) -> T         POST {collection}/{groupId}
 *   update(payload)          -> T         PATCH {single}/{updateId(payload)}
 *   remove(id)               -> void      DELETE {single}/{id}
 *
 * Risultato: un oggetto che soddisfa {@link CrudApi} (il contratto atteso da
 * {@link createCrudThunks}). Le entità con metodi extra (es.
 * `taskApi.updateStatus`) fanno spread di questo e aggiungono i propri:
 *
 * ```ts
 * const base = createCrudApi<Task, TaskDto, ...>({ ... });
 * export const taskApi = { ...base, updateStatus };
 * ```
 *
 * `updateId`/`updateToInput` sono parametrici perché il nome dell'id varia per
 * entità (pipelineId/kanbanId/taskId) e il body PATCH lo costruisce il mapper
 * dell'entità (che ignora l'id anche se presente nel payload).
 */

import type { Page } from '@crm/shared';
import { httpClient } from '../api/httpClient';
import type { CrudApi } from './createCrudThunks';

export interface CrudApiConfig<T, Dto, CreateP, UpdateP> {
  /** Path plurale della collezione, es. `/pipelines`. */
  collection: string;
  /** Path singolare del record, es. `/pipeline`. */
  single: string;
  /** Mapper DTO backend -> modello UI. */
  fromDto: (dto: Dto) => T;
  /** Payload di creazione -> body POST. */
  createToInput: (payload: CreateP) => unknown;
  /** Id del record dal payload di update, per l'URL del PATCH. */
  updateId: (payload: UpdateP) => number;
  /** Payload di update -> body PATCH. */
  updateToInput: (payload: UpdateP) => unknown;
}

export function createCrudApi<T, Dto, CreateP, UpdateP, ListP>(
  config: CrudApiConfig<T, Dto, CreateP, UpdateP>,
): CrudApi<T, CreateP, UpdateP, ListP> {
  const { collection, single, fromDto, createToInput, updateId, updateToInput } = config;

  return {
    getAll: (groupId, params) =>
      httpClient
        .get<Page<Dto>>(`${collection}/${groupId}`, {
          params: params as Record<string, unknown> | undefined,
        })
        .then((r) => ({ ...r.data, items: r.data.items.map(fromDto) })),

    getById: (id) => httpClient.get<Dto>(`${single}/${id}`).then((r) => fromDto(r.data)),

    create: (groupId, payload) =>
      httpClient
        .post<Dto>(`${collection}/${groupId}`, createToInput(payload))
        .then((r) => fromDto(r.data)),

    update: (payload) =>
      httpClient
        .patch<Dto>(`${single}/${updateId(payload)}`, updateToInput(payload))
        .then((r) => fromDto(r.data)),

    remove: (id) => httpClient.delete(`${single}/${id}`).then(() => undefined),
  };
}
